<template>
  <div
    class="dp3-side-panel d-fl-shrink0 d-d-flex d-fd-column d-w0 d-ai-flex-end d-m6 d-my0 d-mx0 d-t0 d-b0 d-of-hidden"
    :class="{
      'dp3-side-panel--partial d-w12 d-ml8': isPartiallyOpen && !showSidePanel,
      'dp3-side-panel--expanded d-w332 d-ml8': isDragHovering && (showSidePanel || isPartiallyOpen),
      'dp3-side-panel--full d-w332 d-ml8': showSidePanel,
    }"
  >
    <!-- <div class="d-w100p d-d-flex d-jc-space-between d-ai-center d-mb16 d-px16">
      <h3 class="d-headline--md d-fw-bold">
        {{ sidePanelContents.length > 0 ? `Pinned (${sidePanelContents.length})` : 'Pinned' }}
      </h3>
      <dt-button
        v-dt-tooltip:left="`Close side panel (Shift+A)`"
        importance="clear"
        kind="muted"
        class="dp3-btn--icon-only"
        @click="toggleSidePanel"
      >
        <template #icon>
          <dt-icon
            name="close"
            size="400"
          />
        </template>
      </dt-button>
    </div> -->

    <!-- Show conversation content if available -->
    <div
      v-if="sidePanelContents.length > 0"
      class="d-fl1 d-p16 d-pl0 d-w100p d-d-flex d-fd-column d-g12 d-of-x-hidden d-of-y-auto d-h100vh d-ps-relative"
    >
      <!-- Multiple conversations -->
      <div
        v-for="(content, index) in sidePanelContents"
        :key="
          content.type === 'call-message'
            ? `call-message_${content.callId}_${content.message?.timestamp}`
            : `${content.conversationType || content.type}_${content.conversationId || content.callId}`
        "
        class="d-d-flex d-fd-column d-ba d-bc-subtle d-bar8 d-bgc-primary"
      >
        <!-- Conversation header -->
        <div class="d-d-flex d-jc-space-between d-ai-center d-p12 d-py8 d-pr8 d-bb d-bc-subtle">
          <div class="d-d-flex d-fd-column d-g2 d-of-hidden">
            <div class="d-headline--md d-lh-300 d-truncate d-fl1 d-d-flex d-ai-center d-g8">
              <!-- Type indicator icon -->
              <template
                v-if="
                  (content.conversationType || content.type) === 'contact' ||
                  (content.conversationType || content.type) === 'direct'
                "
              >
                <ContactAvatar
                  size="sm"
                  :fullName="getConversationDisplayName(content)"
                  :imageSrc="getContactData(content)?.avatar_url"
                  :imageAlt="getConversationDisplayName(content)"
                  :avatarColor="getContactData(content)?.avatar_color"
                  :id="content.conversationId || content.callId"
                />
              </template>
              <template
                v-else-if="
                  (content.conversationType || content.type) === 'group_message' ||
                  (content.conversationType || content.type) === 'group'
                "
              >
                <dt-icon-users size="300" />
              </template>
              <template v-else-if="(content.conversationType || content.type) === 'channel'">
                <dt-icon-hash size="300" />
              </template>
              <template v-else-if="content.type === 'call-message'">
                <dt-icon-phone size="300" />
              </template>
              <span class="d-truncate">
                {{ getConversationDisplayName(content) }}
              </span>
            </div>
            <div class="d-fc-tertiary d-fs-100 d-d-none">
              {{ getMessageTypeLabel(content.conversationType || content.type) }}
            </div>
          </div>
          <dt-button
            v-dt-tooltip:left="`Remove conversation`"
            importance="clear"
            kind="muted"
            class="dp3-btn--icon-only"
            size="sm"
            @click="removeConversation(index)"
          >
            <template #icon>
              <dt-icon name="close" size="300" />
            </template>
          </dt-button>
        </div>
        <!-- Conversation content -->
        <template v-if="content.type === 'call-message'">
          <!-- Call message content - show call pill -->
          <div class="d-p16 d-fl1">
            <div class="d-d-flex d-ai-center d-g8 d-p8 d-bar8 d-bgc-secondary">
              <dt-icon-phone size="200" class="d-fc-primary" />
              <div class="d-d-flex d-fd-column d-fl1">
                <div class="d-fw-semibold">{{ getCallTitle(content) }}</div>
                <div class="d-fs-100 d-fc-tertiary">{{ getCallSubtitle(content) }}</div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <!-- Regular conversation content -->
          <ConversationPreviewContent :item="getItemFromContent(content)" class="d-fl1" />
        </template>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="d-fl1 d-w100p d-d-flex d-fd-column d-ai-center d-jc-center d-ta-center d-fc-muted">
      <dt-icon-pin size="500" />
      <div class="d-fs-200 d-ta-center">Pin Item</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DtButton, DtIcon, DtSkeleton, DtStack } from '@dialpad/dialtone/vue3';
import { DtIconUsers, DtIconHash, DtIconPin, DtIconPhone } from '@dialpad/dialtone-icons/vue3';
import { useSidePanel } from '@/composables/ui/useSidePanel';
import { useDataStore } from '@/store/useDataStore';
import { useSenderInfo } from '@/composables/useSenderInfo';
import ConversationPreviewContent from '@/components/ConversationPreviewContent.vue';
import ContactAvatar from '@/components/ContactAvatar.vue';
import { useDebugLogger } from '@/utils/debugLogger';

const debugLogger = useDebugLogger();

const {
  toggleSidePanel,
  sidePanelContent,
  sidePanelContents,
  conversation,
  conversations,
  removeConversation,
  showSidePanel,
  isDragHovering,
  isPartiallyOpen,
} = useSidePanel();
const dataStore = useDataStore();

// Use centralized sender info functions
const { getSenderName, getSenderAvatar, getSenderAvatarColor } = useSenderInfo();

// Get conversation messages based on content type
const getConversationMessages = (content: any) => {
  const currentUserId = '09f2780b-6785-4b16-b94a-7605eb5cc1ba';
  try {
    // Handle different content types
    const conversationType = content.conversationType || content.type;
    const conversationId = content.conversationId || content.callId;

    if (conversationType === 'contact' || conversationType === 'direct') {
      // Direct messages
      const userContactMessages = dataStore.userContactsMessagesData[currentUserId] || [];
      const thread = userContactMessages.find(c => c.contact_id === conversationId);
      return thread?.messages || [];
    }

    if (conversationType === 'group_message' || conversationType === 'group') {
      // Group messages
      let groupMessages = dataStore.groupMessagesConversationsData?.[conversationId] || [];

      // If not found, try with url_id or slug
      if (groupMessages.length === 0) {
        const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === conversationId);
        if (group) {
          const keys = Object.keys(dataStore.groupMessagesConversationsData || {});
          const matchingKey = keys.find(key => key === group.url_id || key === group.slug || key === group.group_id);
          if (matchingKey) {
            groupMessages = dataStore.groupMessagesConversationsData[matchingKey] || [];
          }
        }
      }

      return groupMessages;
    }

    if (conversationType === 'channel') {
      // Channel messages
      const sharedMessages =
        dataStore.sharedChannelMessagesData?.find(m => m.link_id === conversationId)?.messages || [];
      const personalMessages =
        dataStore.userChannelMessagesData?.[currentUserId]?.find(m => m.link_id === conversationId)?.messages || [];

      // Combine and sort by timestamp
      const combinedMessages = [...sharedMessages, ...personalMessages].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      return combinedMessages;
    }

    return [];
  } catch (error) {
    debugLogger.error('Error getting conversation messages:', error);
    return [];
  }
};

// Create a conversation object for the MessageComposer
const getConversationObject = (content: any) => {
  const conversationType = content.conversationType || content.type;
  const conversationId = content.conversationId || content.callId;

  // For direct messages, find the contact
  if (conversationType === 'contact' || conversationType === 'direct') {
    const contact = dataStore.contactsData?.contacts?.find(c => c.id === conversationId);
    return contact || { id: conversationId, name: content.name || 'Direct Message' };
  }

  // For group messages, find the group
  if (conversationType === 'group_message' || conversationType === 'group') {
    const group = dataStore.groupMessagesData?.group_messages?.find(g => g.id === conversationId);
    return (
      group || {
        id: conversationId,
        name: content.name || 'Group Chat',
        participants: content.participants,
      }
    );
  }

  // For channels, find the channel
  if (conversationType === 'channel') {
    const channel = dataStore.channelsData?.channels?.find(c => c.id === conversationId);
    return channel || { id: conversationId, name: content.name || 'Channel' };
  }

  return { id: conversationId, name: content.name || 'Conversation' };
};

// Format message timestamp
const formatMessageTime = (timestamp: string) => {
  return new Date(timestamp).toLocaleTimeString();
};

const getMessageTypeLabel = (type: string) => {
  switch (type) {
    case 'contact':
      return 'Direct Message';
    case 'channel':
      return 'Channel Message';
    case 'group':
    case 'group_message':
      return 'Group Message';
    case 'contact_center':
      return 'Contact Center';
    default:
      // Try to derive a reasonable label from the type string
      if (type) {
        // Convert camelCase or snake_case to Title Case with spaces
        const formatted = type
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .replace(/^\w/, c => c.toUpperCase())
          .trim();

        return formatted + (formatted.toLowerCase().includes('message') ? '' : ' Message');
      }
      return 'Message';
  }
};

const getConversationDisplayName = (content: any) => {
  if (!content) return 'Conversation';

  const conversationType = content.conversationType;
  const conversationId = content.conversationId;

  if (conversationType === 'contact') {
    // Direct message - find the contact name using ID-based lookup for consistency
    // This ensures the same contact is always identified correctly regardless of source
    const contact = dataStore.contactsData.contacts.find(
      (c: any) => c.id === conversationId || c.link_id === conversationId
    );
    if (contact) {
      // Always use the canonical name from the contact data
      return (
        contact.name ||
        (contact.first_name && contact.last_name
          ? `${contact.first_name} ${contact.last_name}`
          : contact.display_name || contact.email || contact.phone || 'Contact')
      );
    }

    // Try fallback sources if not found in main contacts
    const allContacts = [
      ...dataStore.contactsData.contacts,
      ...(dataStore.contactsData.dialpad_users || []),
      ...(dataStore.userContactsData?.['09f2780b-6785-4b16-b94a-7605eb5cc1ba'] || []),
    ];
    const fallbackContact = allContacts.find(
      (c: any) => c.id === conversationId || c.link_id === conversationId || c.url_id === conversationId
    );
    if (fallbackContact) {
      // Always use the canonical name from the contact data
      return (
        fallbackContact.name ||
        (fallbackContact.first_name && fallbackContact.last_name
          ? `${fallbackContact.first_name} ${fallbackContact.last_name}`
          : fallbackContact.display_name || fallbackContact.email || fallbackContact.phone || 'Contact')
      );
    }

    // Last resort: try to find contact by matching any static HTML references
    // This handles the "Sarah Marita" -> "Sarah Johnson" case
    const knownMappings = {
      // Map any known display name inconsistencies to contact IDs
      'Sarah Marita': 'a5552094-f60e-44ac-96d6-e38ec86219f4',
    };

    // If conversationId matches a known problematic ID, look it up properly
    if (conversationId === 'a5552094-f60e-44ac-96d6-e38ec86219f4') {
      const sarahContact = dataStore.contactsData.contacts.find(
        (c: any) => c.id === 'a5552094-f60e-44ac-96d6-e38ec86219f4'
      );
      if (sarahContact) {
        return sarahContact.name; // Returns "Sarah Johnson"
      }
    }

    return 'Contact';
  }

  if (conversationType === 'channel') {
    // Channel - find the channel name
    const channel = dataStore.channelsData.channels.find(
      (c: any) => c.id === conversationId || c.link_id === conversationId
    );

    if (channel) {
      return `#${channel.name}`;
    }

    // Check user channels
    const currentUserId = '09f2780b-6785-4b16-b94a-7605eb5cc1ba';
    const userChannels = dataStore.userChannelsData[currentUserId] || [];
    const userChannel = userChannels.find((c: any) => c.link_id === conversationId);
    if (userChannel) {
      const channelData = dataStore.channelsData.channels.find((c: any) => c.link_id === userChannel.link_id);
      return channelData ? `#${channelData.name}` : '#Channel';
    }

    return '#Channel';
  }

  if (conversationType === 'group' || conversationType === 'group_message') {
    // The conversationId here is actually the key from groupMessagesConversationsData
    // We need to find the corresponding group in groupMessagesData
    let groupMessage = null;

    // Group finding logic (same as inbox)
    if (conversationId === 'MsJBMTzn-yHLQBl9WBTKJ') {
      groupMessage = dataStore.groupMessagesData?.group_messages?.find(
        g => g.id === '04a644cf-c30b-4a83-aa65-01c803f96aa7'
      );
    } else {
      const allGroups = dataStore.groupMessagesData?.group_messages || [];
      groupMessage = allGroups.find(
        g => g.id === conversationId || g.url_id === conversationId || g.slug === conversationId
      );
      if (!groupMessage) {
        const allConversationKeys = Object.keys(dataStore.groupMessagesConversationsData || {});
        const keyIndex = allConversationKeys.indexOf(conversationId);
        if (keyIndex !== -1 && keyIndex < allGroups.length) {
          groupMessage = allGroups[keyIndex];
        }
      }
    }

    if (groupMessage) {
      // If the group has a custom name, use it
      if (groupMessage.name && groupMessage.name.trim() !== '') {
        return groupMessage.name;
      }

      // Otherwise, build name from participants
      const currentUserId = '09f2780b-6785-4b16-b94a-7605eb5cc1ba'; // TODO: Get from auth
      const participantObjects = groupMessage.participants
        .map(id => dataStore.contactsData?.contacts?.find(c => c.id === id))
        .filter(contact => contact && contact.id !== currentUserId);

      // Sort participant objects by their full name alphabetically (same as sidebar)
      participantObjects.sort((p1, p2) => (p1.name || '').localeCompare(p2.name || ''));

      const firstNames = participantObjects.map(p => p.name?.split(' ')[0] || 'Unknown');

      if (firstNames.length > 0) {
        if (firstNames.length <= 8) {
          return firstNames.join(', ');
        } else {
          const displayNames = firstNames.slice(0, 8);
          const remaining = firstNames.length - 8;
          return `${displayNames.join(', ')}, and ${remaining} other${remaining > 1 ? 's' : ''}`;
        }
      }
    }

    return 'Group Chat';
  }

  // Handle call messages
  if (content.type === 'call-message') {
    // For call messages, try to get the contact name from the call data
    if (content.message && content.message.senderId) {
      const contact = dataStore.contactsData?.contacts?.find(c => c.id === content.message.senderId);
      if (contact) {
        return contact.name || 'Call';
      }
    }
    return 'Call';
  }

  return 'Conversation';
};

const getItemFromContent = (content: any) => {
  // Convert SidePanel content format to ConversationPreviewContent item format
  const conversationType = content.conversationType || content.type;
  const conversationId = content.conversationId || content.callId;

  // Map conversation type to itemType
  let itemType = 'contact'; // default
  if (conversationType === 'group_message' || conversationType === 'group') {
    itemType = 'group_message';
  } else if (conversationType === 'channel') {
    itemType = 'channel';
  } else if (conversationType === 'contact' || conversationType === 'direct') {
    itemType = 'contact';
  }

  return {
    id: conversationId,
    itemType: itemType,
    name: content.name || getConversationDisplayName(content),
    participants: content.participants,
  };
};

// Get contact data for a given content item
const getContactData = (content: any) => {
  const conversationId = content.conversationId || content.callId;
  if (!conversationId) return null;

  return dataStore.contactsData?.contacts?.find(c => c.id === conversationId) || null;
};

// Get call title for call message display
const getCallTitle = (content: any) => {
  if (!content.message) return 'Call';

  const callData = content.message.call_data;
  if (!callData) return 'Call';

  const direction = callData.direction === 'incoming' ? 'Incoming' : 'Outgoing';
  return `${direction} call`;
};

// Get call subtitle for call message display
const getCallSubtitle = (content: any) => {
  if (!content.message) return '';

  const callData = content.message.call_data;
  if (!callData) return '';

  const parts = [];

  if (callData.duration) {
    parts.push(callData.duration);
  }

  if (callData.status === 'missed') {
    parts.push('Missed');
  }

  if (callData.summary?.title) {
    parts.push(callData.summary.title);
  }

  return parts.join(' • ');
};
</script>

<style>
.dp3-side-panel {
  background: var(--dp3-background-color);
}
html[data-dt-theme='dark'][data-dt-brand='dp']:not([data-color-theme]) .dp3-side-panel {
  background: var(--dt-color-surface-secondary);
}

/* Default full state */
.dp3-side-panel--full {
  width: 332px;
  transition: all 0.3s ease-in-out;
}

/* Partial state - when side panel is closed but dragging starts */
.dp3-side-panel--partial {
  width: 0px;
  transition: all 0.3s ease-in-out;
}
.dp3-side-panel.dp3-side-panel--partial::after {
  opacity: 1;
}

.dp3-side-panel.dp3-side-panel--full::after {
  right: 16px;
}

.dp3-side-panel.dp3-side-panel--expanded::after {
  opacity: 0;
  right: 16px;
}

/* Hide content when partially open */
.dp3-side-panel--partial .d-fl1 {
  opacity: 0;
  pointer-events: none;
}

/* Show content when expanded */
.dp3-side-panel--expanded .d-fl1 {
  opacity: 1;
  pointer-events: auto;
}

.dp3-global-drop-zone {
  pointer-events: auto;
  transition: all 0.3s ease-in-out;
  border: 3px solid var(--dt-color-border-brand);
  border-radius: 12px !important;
  right: 12px;
  top: 12px;
  bottom: 4px;
  opacity: 1;
  height: auto;
  width: 324px;
}

/* Partial state - when side panel is closed but dragging starts */
.dp3-global-drop-zone--partial {
  border: 3px solid transparent !important;
  /* border-right:3px solid var(--dt-color-border-brand) !important; */
  opacity: 1;
  height: auto;
  right: 8px;
  top: 12px;
  bottom: 4px;
  position: relative;
  width: 200px;
}
.dp3-global-drop-zone::after {
  opacity: 0;
  right: 130px;
}
.dp3-global-drop-zone.dp3-global-drop-zone--partial::after {
  content: '';
  width: 3px;
  border-radius: 3px;
  background: var(--dt-color-brand-purple);
  height: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: auto;
  opacity: 1;
  transition: all 0.2s ease-in-out;
  transition-delay: 0.2s;
}

.dp3-global-drop-zone.dp3-global-drop-zone--expanded,
.dp3-global-drop-zone.dp3-global-drop-zone--full {
  pointer-events: auto;
  transition: all 0.3s ease-in;
  border: 3px solid var(--dt-color-border-brand) !important;
  border-radius: 12px !important;
  right: 12px;
  top: 12px;
  bottom: 4px;
  opacity: 1;
  height: auto;
  width: 324px;
}
.dp3-global-drop-zone.dp3-global-drop-zone--expanded::after,
.dp3-global-drop-zone.dp3-global-drop-zone--full::after {
  opacity: 0;
  transition: all 0.1s ease-out;
}

.dp3-global-drop-zone:hover,
.dp3-global-drop-zone--active {
  opacity: 1;
}
</style>
